<template>
  <va-card id="availabilities-page">
    <div class="d-flex justify-content-between align-items-center">
      <h3 class="mb-0">
        {{$t('agenda.availabilities.title')}}
      </h3>
    </div>

    <div class="row">
        <div class="col">
          <div>
            <label class="form-label">{{$t('clinical.appointments.select_facility')}}:</label>
            <div class="form-group" style="margin-bottom:1.25rem;">
              <div class="input-group">
                <multiselect
                  v-model="selectedFacilities"
                  :options="facilities"
                  track-by="id"
                  label="name"
                  select-label=""
                  selected-label=""
                  deselect-label=""
                  placeholder="---"
                  :allow-empty="true"
                  :multiple="true"
                  :searchable="true"
                  >
                </multiselect>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <FullCalendar
            :key="calendarKey"
            ref="fullCalendar"
            :options="calendarOptions">
            <template v-slot:eventContent='data'>
              <EventDisplay
                :date="data.timeText"
                :view="data.view"
                :event="data.event"
                />
            </template>
          </FullCalendar>
        </div>
      </div>

      <AvailabilityForm
        v-if="showAvailabilityForm"
        v-bind="propsAvailabilityForm"
        @modal-closed="modalClosed"
        @availability-created="updateCalendar"
        @availability-updated="updateCalendar"
        @availability-deleted="updateCalendar" />

  </va-card>
</template>
<script>
import moment from 'moment'
import EventDisplay from './EventDisplay'
import FullCalendar from '@fullcalendar/vue'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import momentPlugin from '@fullcalendar/moment'
import AvailabilityForm from './AvailabilityForm'
import { FacilityService, AvailabilityService } from '@/api/BookingService'
import Multiselect from 'vue-multiselect'
import LocalStorageHelper from '@/local-storage/LocalStorageHelper'
import { FILTERS_CALENDAR_FACILITIES, FILTERS_CALENDAR_DAY, FILTERS_CALENDAR_VIEW } from '@/local-storage/LocalStorageKeys'
import { mapGetters } from 'vuex'
export default {
  components: {
    EventDisplay,
    FullCalendar,
    Multiselect,
    AvailabilityForm
  },
  data () {
    return {
      selectedFacilities: [],
      facilities: [],
      facilitiesAdmin: [],
      hasLoaded: false,
      calendarKey: 0,
      propsAvailabilityForm: {
        facilities: [],
        availableActivities: [],
        selectedAvailability: null,
        fromEvent: {
          start: null,
          end: null
        }
      },
      showAvailabilityForm: false,
      calendarOptions: {
        eventSources: [{
          events: (options, callback) => {
            let params = {
              startDate: moment(options.start).format('YYYY-MM-DD'),
              endDate: moment(options.end).format('YYYY-MM-DD'),
              facilities: this.selectedFacilities.map(facility => facility.id).join(',')
            }
            AvailabilityService.query(params)
              .then(availabilities => {
                availabilities = availabilities.map(availability => {
                  return {
                    ...availability,
                    start: this.$options.filters.localTime(availability.start),
                    end: this.$options.filters.localTime(availability.end)
                  }
                })
                callback(availabilities)
              })
              .catch(errors => console.log('errors', errors))
          }
        }],
        plugins: [dayGridPlugin, timeGridPlugin, momentPlugin, interactionPlugin],
        dayMaxEventRows: 2,
        headerToolbar: {
          left: 'prev next today',
          center: 'title',
          right: 'timeGridDay timeGridWeek dayGridMonth'
        },
        eventColor: '#29c4a9',
        eventDisplay: 'block',
        buttonText: {
          today: this.$t('agenda.buttons.today'),
          month: this.$t('agenda.buttons.month'),
          week: this.$t('agenda.buttons.week'),
          day: this.$t('agenda.buttons.day'),
          listMonth: this.$t('agenda.buttons.listMonth')
        },
        customButtons: {
          timeGridDay: {
            text: this.$t('agenda.buttons.day'),
            click: (event) => {
              this.calendarAPI.gotoDate(this.getNextViewStart())
              this.calendarAPI.changeView('timeGridDay')
            }
          },
          timeGridWeek: {
            text: this.$t('agenda.buttons.week'),
            click: () => {
              this.calendarAPI.gotoDate(this.getNextViewStart())
              this.calendarAPI.changeView('timeGridWeek')
            }
          }
        },
        navLinks: true,
        dayHeaderContent: (content) => {
          return { html: `
            <div class="calendar__header__label">
              <div class="calendar__header__label__day view__${content.view.type}">` + moment(content.date).locale(this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage).format('DD') + `</div>
              <div class="calendar__header__label__weekday">` + moment(content.date).locale(this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage).format('dddd') + `</div>
            </div>
          ` }
        },
        titleFormat: 'MMMM, YYYY',
        initialDate: LocalStorageHelper.get(FILTERS_CALENDAR_DAY, false),
        initialView: LocalStorageHelper.get(FILTERS_CALENDAR_VIEW) || 'dayGridMonth',
        locale: this.selectedLanguage,
        allDayContent: this.$t('agenda.buttons.all-day'),
        allDayClassNames: 'calendar__allday__label',
        slotMinTime: '09:00:00',
        firstDay: 1,
        slotMaxTime: '23:00:00',
        displayEventEnd: true,
        nowIndicator: true,
        slotDuration: '00:30',
        slotLabelFormat: 'HH:mm',
        slotLabelInterval: '01:00',
        height: 'auto',
        dayHeaderFormat: 'ddd D',
        eventTimeFormat: 'H:mm',
        editable: true,
        weekends: true,
        selectable: true,
        selectMirror: true,
        unselectAuto: true,
        dateClick: this.eventCreated,
        eventClick: this.eventSelected,
        eventDrop: this.updateEventFromUI,
        eventResize: this.updateEventFromUI,
        select: this.eventCreated,
        datesDidUpdate: this.datesDidUpdate
      },
      calendarAPI: null
    }
  },
  created () {
    this.loadFacilities()
    this.loadFacilitiesAdmin()
    this.loadActivities()
  },
  mounted () {
    this.calendarAPI = this.$refs.fullCalendar.getApi()
    this.loadEvents()
  },
  methods: {
    loadCalendarOptions: function () {
      if (this.calendarAPI) {
        this.calendarAPI.setOption('locale', this.selectedLanguage === 'gb' ? 'en' : this.selectedLanguage)
      }
    },
    getNextViewStart: function () {
      let start = moment(this.calendarAPI.view.currentStart)
      let end = moment(this.calendarAPI.view.currentEnd)
      let now = moment()
      if (start.isSameOrBefore(now) && end.isSameOrAfter(now)) {
        return now.toISOString()
      }
      return start.toISOString()
    },
    datesDidUpdate: function () {
      this.$nextTick(() => {
        //console.log(this.calendarAPI.view.currentStart)
        LocalStorageHelper.set(FILTERS_CALENDAR_VIEW, this.calendarAPI.view.type)
        LocalStorageHelper.set(FILTERS_CALENDAR_DAY, moment(this.calendarAPI.view.currentStart).format('YYYY-MM-DD'), false)
      })
    },
    updateCalendar: function () {
      this.showAvailabilityForm = false
      this.propsAvailabilityForm = {}
      this.loadEvents()
    },
    modalClosed: function () {
      this.propsAvailabilityForm = {}
      this.showAvailabilityForm = false
    },
    updateEventFromUI ({ event, revert }) {
      if (!this.hasPermissionToEditEvent(event)) {
        revert()
        return
      }
      let params = {
        start: this.$options.filters.utcTime(event.start),
        end: this.$options.filters.utcTime(event.end),
      }
      AvailabilityService.update(event.id, params)
        .then(availability => {
          this.showToast(this.$t('agenda.availability.validationMessages.update_msg'), this.$store.state.successToastOptions)
        }).catch(err => {
          console.log(err)
          revert()
        })
    },
    eventCreated: function ({ start, end, allDay }) {
      let facilities = this.selectedFacilities.length ? this.selectedFacilities.filter(facility => this.facilitiesAdmin.some(_facility => facility.id === _facility.id)) : this.facilitiesAdmin
      if (!facilities.length) return
      let activeFacilities = facilities.filter(facility => facility.entity.status !== 2)
      if (!activeFacilities.length) return
      start = moment(start)
      end = moment(end)
      this.propsAvailabilityForm = {
        fromEvent: {
          start: allDay ? moment(`${start.format('YYYY-MM-DD')} ${this.calendarOptions.slotMinTime}`).format('YYYY-MM-DD HH:mm:ss') : `${start.format('YYYY-MM-DD HH:mm:ss')}`,
          end: allDay ? moment(`${end.format('YYYY-MM-DD')} ${this.calendarOptions.slotMaxTime}`).format('YYYY-MM-DD HH:mm:ss') : `${end.format('YYYY-MM-DD HH:mm:ss')}`
        },
        facilities,
        availableActivities: this.activities
      }
      this.showAvailabilityForm = true
    },
    eventSelected: function ({ event }) {
      if (!this.hasPermissionToEditEvent(event)) return
      let facilities = this.selectedFacilities.length ? this.selectedFacilities.filter(facility => this.facilitiesAdmin.some(_facility => facility.id === _facility.id)) : this.facilitiesAdmin
      this.propsAvailabilityForm = {
        selectedAvailability: {
          id: event.id,
          ...event.extendedProps,
          source: null,
          start: moment(event.start).format('YYYY-MM-DD HH:mm:ss'),
          end: moment(event.end).format('YYYY-MM-DD HH:mm:ss'),
          public: !event.extendedProps.private
        },
        facilities,
        availableActivities: this.activities
      }
      this.showAvailabilityForm = true
    },
    loadEvents: function () {
      this.calendarAPI.refetchEvents()
    },
    hasPermissionToEditEvent: function (event) {
      return this.facilitiesAdmin.some(facility => facility.id === event.extendedProps.facility.id)
    },
    loadFacilities: function () {
      FacilityService.access({ access: 1, info: 200 })
        .then(facilities => {
          this.facilities = facilities.map(facility => ({ ...facility, title: facility.name }))
          this.updateFromLocalStorage()
            .then(() => this.loadCalendarOptions())
        }).catch(errors => {
          console.log('errors', errors)
        })
    },
    loadFacilitiesAdmin: function () {
      FacilityService.access({ access: 3, info: 200 })
        .then(facilities => {
          this.facilitiesAdmin = facilities
        }).catch(errors => {
          console.log('errors', errors)
        })
    },
    loadActivities: function () {
      this.$http({
        url: this.$store.state.evollauth_server + '/activities'
      }).then((response) => {
        this.activities = response.data.data
      }).catch((error) => {
        console.log(error)
      })
    },
    updateFromLocalStorage: function () {
      let previousFacilities = LocalStorageHelper.get(FILTERS_CALENDAR_FACILITIES)
      if (previousFacilities && previousFacilities.length) {
        this.selectedFacilities = this.facilities.filter(facility => previousFacilities.includes(facility.id))
      }
      this.hasLoaded = true
      return Promise.resolve()
    },
    nameWithCity ({ name, address }) {
      if (!address) return name
      return name + ' (' + address.city + ')'
    },
  },
  computed: {
    ...mapGetters([
      'sidebarMinimized'
    ]),
    selectedLanguage: function () {
      return this.$i18n.locale()
    }
  },
  watch: {
    selectedLanguage: function (language) {
      if (this.calendarAPI) {
        this.calendarAPI.setOption('locale', language === 'gb' ? 'en' : language)
      }
    },
    selectedFacilities: function (facilities) {
      this.loadEvents()
      LocalStorageHelper.set(FILTERS_CALENDAR_FACILITIES, facilities.map(item => item.id))
    },
    sidebarMinimized: function () {
      if (this.calendarAPI) {
        this.calendarKey = this.calendarKey + 1
      }
    }
  }
}
</script>
<style lang='scss'>
// you must include each plugins' css
// paths prefixed with ~ signify node_modules
// @import '~@fullcalendar/core/main.css';
// @import '~@fullcalendar/daygrid/main.css';
// @import '~@fullcalendar/timegrid/main.css';

#availabilities-page {
  .fc-day-grid-event > .fc-content {
    white-space: normal;
    text-overflow: ellipsis;
    max-height:20px;
  }
  .fc-day-grid-event > .fc-content:hover {
    max-height:none !important;
  }
  .calendar__allday__label {
    font-size: 0.9rem;
  }

  .fc-header-left, fc-header-center, fc-header-right {
      width: 100%;
      display: block;
  }

  @mixin for-desktop {
    @media (min-width: 599px) {
      @content;
    }
  }

  .fc-day-grid-event > .fc-content {
    white-space: normal;
    text-overflow: ellipsis;
    max-height:20px;
  }
  .fc-day-grid-event > .fc-content:hover {
    max-height:none !important;
  }
  .calendar__allday__label {
    font-size: 0.9rem;
  }

  .fc-header-left, fc-header-center, fc-header-right {
      width: 100%;
      display: block;
  }

  .floating__button {
    position: fixed;
    z-index: 9999;
    color: #29c4a9;
    font-size: 2rem;
  }

  .calendar__header__label {
    display: flex;
    flex-direction: column;
    justify-content:center;
    align-items: center;

    & .calendar__header__label__weekday {
      font-size: 0.6rem;
      text-transform: uppercase;
      @include for-desktop {
        font-size: 1.3rem;
      }
    }

    & .calendar__header__label__day {
      font-size: 0.8rem;
      font-weight: bold;
      @include for-desktop {
        font-size: 1.8rem;
      }

      &.view__dayGridMonth {
        display: none !important;
      }
    }
  }

  th.fc-day-today {
    background-color: #e0e0e0;
  }

  table.fc-scrollgrid {
    border: 0.5px solid #d3d3d3 !important;
    // border-style: double !important;
    // border-width: 0px !important;
    // border-color: #aaa !important;
  }

  // .fc td, .fc th, .fc-timegrid, .fc-timeGridWeek-view, .fc-view {
  //   border-style: double !important;
  //   border-width: 0px !important;
  //   border-color: #aaa !important;
  // }

  td.fc-timegrid-slot {
    border: 0.5px solid #d3d3d3 !important;
  }

  .fc-col-header-cell {
    border-style: double !important;
    border-width: 0px !important;
    border-color: #aaa !important;
  }

  .fc-scrollgrid-section-header {
    border-left-style: double !important;
    border-left-width: 0px !important;
    border-left-color: #aaa !important;
  }

  .fc-timegrid-slot {
    height: 50px !important;
  }

  tr:first-child {
    border: none !important;
  }

  .fc-timegrid-slot-label{
    border: none !important;
  }

  .fc-button-primary {
    color: #fff;
    background-color: #29c4a9;
    border-color: #29c4a9;
  }
}
</style>
